import isPropValid from "@emotion/is-prop-valid";
import { type AgendaPreview, googleFontsUrl, SitePage } from "@keepoinc/site-page-react-component";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes, StyleSheetManager } from "styled-components";

import { PreviewHint } from "../../contexts/PreviewSite";
import { useSitePageContextProps } from "../../hooks/SitePageContextProps";
import { Site } from "../../model/site";
import { getThemeFromSite } from "../../model/theme";
import { setStylesheet } from "../../utils/fonts";
import { KeepoLoader } from "../KeepoLoader";
import { Container as LoadingCard } from "../LoadingModal/styles";
import { blockPageLinks } from "./blockPageLinks";

const IFRAME_DOCUMENT = '<!DOCTYPE html><html><head><meta charset="utf-8" /></head><body></body></html>';

// Reference phone: iPhone 11 Pro Max screen (CSS px). The Keepo is laid out at this exact size and the
// whole phone is then scaled to fit the preview column, so it reads as it does on a real device.
const DEVICE_WIDTH = 414;
const DEVICE_HEIGHT = 896;
const BEZEL = 8;
const PHONE_WIDTH = DEVICE_WIDTH + 2 * BEZEL;
const PHONE_HEIGHT = DEVICE_HEIGHT + 2 * BEZEL;

/* Fills the preview column; measured to compute the phone's scale */
const Stage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 0;
`;

/* Same sheen as the templates' thumbnails while their images load */
const shimmer = keyframes`
  from {
    background-position: 150% 0;
  }
  to {
    background-position: -50% 0;
  }
`;

/* Over the Keepo while the screen loads what to show: a light sheen sweeping a dimmed layer (as on
   the loading thumbnails) and the Keepo symbol breathing in a card, as the app's big loading */
const Loading = styled.div<{ $visible: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: linear-gradient(100deg, transparent 30%, rgba(255, 255, 255, 0.06) 50%, transparent 70%)
    rgba(24, 26, 32, 0.75);
  background-size: 200% 100%;
  animation: ${shimmer} 1.2s linear infinite;
  /* Quick fade both ways; hidden (and not animating) once faded out */
  opacity: ${props => (props.$visible ? 1 : 0)};
  visibility: ${props => (props.$visible ? "visible" : "hidden")};
  transition: ${props =>
    props.$visible ? "opacity 0.15s ease-out" : "opacity 0.2s ease-in, visibility 0s linear 0.2s"};
  pointer-events: ${props => (props.$visible ? "auto" : "none")};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* In place of the Keepo (hidden underneath, fixed footer included) when there's nothing to show yet */
const Hint = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  background: var(--color-neutral-darkness);

  svg {
    width: 40px;
    height: 40px;
    margin-bottom: 0.5rem;
    color: var(--color-neutral-150);
  }

  h4 {
    font-family: Poppins, sans-serif;
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--color-neutral-white);
  }

  p {
    max-width: 240px;
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--color-neutral-150);
  }
`;

const caretBlink = keyframes`
  50% {
    opacity: 0;
  }
`;

/* A browser's address bar on top of the Keepo: slides down (pushing it) when there's an address */
const AddressBar = styled.div<{ $visible: boolean }>`
  flex-shrink: 0;
  height: ${props => (props.$visible ? "52px" : "0")};
  overflow: hidden;
  /* The phone's own color (its bezel), so it reads as part of the device */
  background: var(--color-neutral-base);
  transition: height 0.35s cubic-bezier(0.22, 1, 0.36, 1);

  .field {
    display: flex;
    gap: 6px;
    align-items: center;
    height: 36px;
    padding: 0 14px;
    margin: 8px 12px;
    font-family: Inter, sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: var(--color-neutral-white);
    white-space: nowrap;
    background: var(--color-neutral-dark);
    border-radius: 100px;
  }

  /* Tall enough for the descenders (g, p, j) not to be cut */
  .url {
    overflow: hidden;
    line-height: 20px;
    text-overflow: ellipsis;
  }

  .caret {
    flex-shrink: 0;
    width: 1.5px;
    height: 16px;
    margin-left: -4px;
    background: var(--color-neutral-white);
    animation: ${caretBlink} 1s steps(1) infinite;
  }
`;

const LockIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--color-neutral-150)"
    strokeWidth={2.4}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

/* Takes the phone's scaled size in the layout (a transform alone doesn't shrink the box) */
const Scaled = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const Frame = styled.div<{ $fullscreen?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  iframe {
    display: block;
    flex: 1;
    width: 100%;
    min-height: 0;
    border: 0;
  }

  ${props =>
    props.$fullscreen
      ? /* Mobile preview overlay: the Keepo fills the available area, no phone frame */
        `
      width: 100%;
      height: 100%;
    `
      : `
      position: absolute;
      top: 0;
      left: 0;
      width: ${PHONE_WIDTH}px;
      height: ${PHONE_HEIGHT}px;
      border: ${BEZEL}px solid var(--color-neutral-base);
      /* Same as the bezel: where the screen's rounded edge meets the address bar, no darker seam shows */
      background: var(--color-neutral-base);
      border-radius: 48px;
      box-shadow: 0 24px 48px rgba(0, 0, 0, 0.4);
      transform-origin: top left;
    `}
`;

type Props = {
  site: Site;
  fullscreen?: boolean;
  /** A dimmed layer with a spinner over the Keepo (the screen is loading what to show). */
  loading?: boolean;
  /** A message shown instead of the Keepo (e.g. pick a template to preview it). */
  hint?: PreviewHint;
  /** A browser address bar on top of the Keepo, showing this address (hidden when empty). */
  address?: string;
  /** A link to one of the Keepo's pages (the menu's) was clicked: its address, e.g. "/contato". */
  onOpenPage?: (path: string) => void;
  /** The owner's own services and days on the agenda page (see useAgendaPreview); none: an empty agenda. */
  agendaPreview?: AgendaPreview;
};

/**
 * Renders the Keepo inside an iframe so the site component gets a phone-sized viewport:
 * its media queries and fixed elements (floating WhatsApp, form modal) behave as they do
 * on the published page instead of reacting to the editor window.
 */
export const LivePreview = ({ site, fullscreen, loading, hint, address, onOpenPage, agendaPreview }: Props) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeDocument, setIframeDocument] = useState<Document>();

  // Largest scale at which the whole phone fits the column (never above its real size)
  const stageRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const stage = stageRef.current;
    if (fullscreen || !stage) {
      return;
    }
    const fit = () => setScale(Math.min(1, stage.clientWidth / PHONE_WIDTH, stage.clientHeight / PHONE_HEIGHT) || 1);
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [fullscreen]);

  const theme = getThemeFromSite(site);
  const baseContextProps = useSitePageContextProps();
  const contextProps = agendaPreview ? { ...baseContextProps, agenda: { preview: agendaPreview } } : baseContextProps;

  const onIframeLoad = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) {
      return;
    }

    // Fonts, Tailwind and the site component globals come from the editor document.
    // styled-components rules are injected straight into the iframe by the StyleSheetManager.
    document.head
      .querySelectorAll('link[rel="stylesheet"], style:not([data-styled])')
      .forEach(node => doc.head.appendChild(node.cloneNode(true)));

    setIframeDocument(doc);
  }, []);

  useEffect(() => {
    if (iframeDocument) {
      iframeDocument.body.style.backgroundColor = theme.primary;
    }
  }, [iframeDocument, theme.primary]);

  // The Keepo's fonts (titles and text), as the published page loads them: the new ones as soon as they change
  const fontsUrl = googleFontsUrl(site.fonts);
  useEffect(() => {
    if (iframeDocument) {
      setStylesheet(iframeDocument, "keepo", fontsUrl);
    }
  }, [iframeDocument, fontsUrl]);

  // Links to pages of the Keepo (the menu) don't navigate the preview away from the Keepo: it shows the page instead
  useEffect(() => {
    if (!iframeDocument) {
      return;
    }
    return blockPageLinks(iframeDocument, path => {
      iframeDocument.defaultView?.scrollTo({ top: 0 });
      onOpenPage?.(path);
    });
  }, [iframeDocument, onOpenPage]);

  // What's coming (e.g. another template) starts at the top: jump there while the loading layer covers it
  useEffect(() => {
    if (loading) {
      iframeDocument?.defaultView?.scrollTo({ top: 0 });
    }
  }, [loading, iframeDocument]);

  const frame = (
    <Frame $fullscreen={fullscreen} style={fullscreen ? undefined : { transform: `scale(${scale})` }}>
      <AddressBar $visible={!!address} aria-hidden={!address}>
        <span className="field">
          <LockIcon />
          <span className="url">{address}</span>
          <span className="caret" />
        </span>
      </AddressBar>
      <iframe ref={iframeRef} srcDoc={IFRAME_DOCUMENT} onLoad={onIframeLoad} title="preview" />
      {iframeDocument &&
        !hint &&
        createPortal(
          <StyleSheetManager target={iframeDocument.head} shouldForwardProp={isPropValid}>
            <div className="ksp">
              <SitePage
                site={site}
                theme={theme}
                contextProps={contextProps}
                layout={site.layout || { align: "LEFT" }}
              />
            </div>
          </StyleSheetManager>,
          iframeDocument.body
        )}
      {hint && (
        <Hint>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
            <rect x="3" y="3" width="7" height="11" rx="1.5" />
            <rect x="14" y="3" width="7" height="11" rx="1.5" />
            <path d="M3 18h7M14 18h7M3 21h4M14 21h4" strokeLinecap="round" />
          </svg>
          <h4>{hint.title}</h4>
          <p>{hint.text}</p>
        </Hint>
      )}
      <Loading $visible={!!loading} role="status" aria-label="Loading" aria-hidden={!loading}>
        {/* Same card as the app's big loading */}
        <LoadingCard>
          <KeepoLoader size={40} color="var(--color-neutral-white)" />
        </LoadingCard>
      </Loading>
    </Frame>
  );

  if (fullscreen) {
    return frame;
  }

  return (
    <Stage ref={stageRef}>
      <Scaled style={{ width: PHONE_WIDTH * scale, height: PHONE_HEIGHT * scale }}>{frame}</Scaled>
    </Stage>
  );
};
